/* global globalThis */
import { createResponseCryptoSession } from './responseCryptoSession'

const PUBLIC_KEY_HEADER = 'X-Response-Public-Key'
const REQUEST_ID_HEADER = 'X-Response-Request-Id'
const CONTEXT_KEY = '__nhResponseEncryption'

export function isBackendRequest(config, defaultBaseURL, locationHref) {
  if (['head', 'options'].includes((config.method || 'get').toLowerCase())) return false
  const backend = new URL(defaultBaseURL || '/', locationHref)
  const base = new URL(config.baseURL || defaultBaseURL || '/', locationHref)
  const raw = config.url || ''
  const target = /^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(raw)
    ? new URL(raw, locationHref)
    : new URL(base.href.replace(/\/$/, '') + '/' + raw.replace(/^\//, ''))
  const prefix = backend.pathname.replace(/\/$/, '')
  return target.origin === backend.origin && (target.pathname === prefix || target.pathname.startsWith(prefix + '/'))
}

function protocolError(response, message) {
  const error = new Error(message)
  error.code = 'RESPONSE_DECRYPTION_FAILED'
  error.response = { status: response.status, data: { msg: message }, config: response.config }
  return error
}

export function installResponseEncryption(axios, {
  enabled = false,
  cryptoApi = globalThis.crypto,
  locationHref = globalThis.location ? globalThis.location.href : 'http://localhost/',
  loadForge
} = {}) {
  if (!enabled) return
  let sessionPromise
  const session = () => {
    if (!sessionPromise) sessionPromise = createResponseCryptoSession(cryptoApi, loadForge)
      .catch(error => { sessionPromise = undefined; throw error })
    return sessionPromise
  }

  axios.interceptors.request.use(async config => {
    if (!isBackendRequest(config, axios.defaults.baseURL, locationHref)) return config
    const keys = await session()
    const requestId = Array.from(cryptoApi.getRandomValues(new Uint8Array(16)),
      byte => byte.toString(16).padStart(2, '0')).join('')
    config.headers = config.headers || {}
    config.headers[PUBLIC_KEY_HEADER] = keys.publicKey
    config.headers[REQUEST_ID_HEADER] = requestId
    config[CONTEXT_KEY] = { requestId }
    return config
  })

  async function decryptResponse(response) {
    const context = response.config && response.config[CONTEXT_KEY]
    if (!context) return response
    const contentType = String(response.headers && response.headers['content-type'] || '').toLowerCase()
    const responseType = response.config.responseType
    let envelope = response.data
    if (responseType === 'blob' || responseType === 'arraybuffer') {
      if (!contentType.includes('json')) return response
      const text = responseType === 'blob' ? await envelope.text() : new TextDecoder().decode(envelope)
      try { envelope = JSON.parse(text) } catch (_) { throw protocolError(response, '接口返回了无效 JSON。') }
    } else if (typeof envelope === 'string') {
      if (!envelope) return response
      try { envelope = JSON.parse(envelope) } catch (_) {
        if (!contentType.includes('json')) return response
        throw protocolError(response, '接口返回了无效 JSON。')
      }
    }
    if ([204, 205, 304].includes(response.status) || envelope === undefined || envelope === null && !contentType.includes('json')) return response
    if (!envelope || envelope.encrypted !== true) {
      if ([401, 403, 502, 503, 504].includes(response.status)) return response
      throw protocolError(response, '接口未返回加密数据，请确认后端已启用响应加密并重启。')
    }
    try {
      if (envelope.version !== 1 || envelope.algorithm !== 'AES-256-GCM'
          || envelope.keyAlgorithm !== 'RSA-OAEP-256' || envelope.requestId !== context.requestId) {
        throw new Error('Encrypted response metadata mismatch')
      }
      const bytes = await (await session()).decrypt(envelope, response.status)
      if (responseType === 'blob') response.data = new Blob([bytes], { type: 'application/json' })
      else if (responseType === 'arraybuffer') response.data = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)
      else {
        const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
        response.data = responseType === 'text' ? text : JSON.parse(text)
      }
      return response
    } catch (_) { throw protocolError(response, '响应解密或完整性校验失败，请刷新页面后重试。') }
  }

  axios.interceptors.response.use(decryptResponse, async error => {
    if (error.response) await decryptResponse(error.response)
    throw error
  })
}

export function createEncryptedUploadRequest(axios, originalUpload, locationHref = globalThis.location.href) {
  return options => {
    if (!isBackendRequest({ method: 'post', url: options.action }, axios.defaults.baseURL, locationHref)) return originalUpload(options)
    const form = new FormData()
    Object.entries(options.data || {}).forEach(([key, value]) => form.append(key, value))
    form.append(options.filename, options.file, options.file.name)
    const controller = new AbortController()
    axios.post(options.action, form, {
      headers: options.headers, signal: controller.signal, preserveUploadHeaders: true,
      withCredentials: Boolean(options.withCredentials),
      onUploadProgress: event => options.onProgress({ ...event, percent: event.total ? event.loaded / event.total * 100 : 0 })
    }).then(result => options.onSuccess(result.data), error => options.onError(error))
    return { abort: () => controller.abort() }
  }
}
